import React, { useState, useEffect } from 'react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Label } from '../ui/Label';
import { Textarea } from '../ui/Textarea';
import { getCurrentUser } from '../../api/authService';
import { registrationService } from '../../api/registrationService';
import { fileService } from '../../api/fileService';
import toast from 'react-hot-toast';
import { 
  FileText, 
  X, 
  Plus,
  Tag,
  Save,
  Paperclip,
  Upload,
  Trash2
} from 'lucide-react';

const CreateArticleModal = ({ isOpen, onClose, onSave, categories = [], article = null }) => {
  const [formData, setFormData] = useState({
    title: '',
    question: '',
    answer: '',
    category: '',
    tags: '',
    status: 'draft',
    roleId: null
  });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [roles, setRoles] = useState([]);
  const [loadingRoles, setLoadingRoles] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const rolesLoadedRef = React.useRef(false);

  // Load roles only when modal opens (prevent duplicate calls)
  useEffect(() => {
    if (isOpen && !rolesLoadedRef.current) {
      loadRoles();
      rolesLoadedRef.current = true;
    }
    // Reset when modal closes
    if (!isOpen) {
      rolesLoadedRef.current = false;
    }
  }, [isOpen]);

  // Reset form when modal opens/closes or article changes
  useEffect(() => {
    if (isOpen) {
      if (article) {
        // Edit mode - populate form with article data
        setFormData({
          title: article.title || '',
          question: article.question || '',
          answer: article.answer || article.content || '',
          category: article.category || '',
          tags: Array.isArray(article.tags) ? article.tags.join(', ') : '',
          status: article.status || 'draft',
          roleId: article.roleId || null
        });
      } else {
        // Create mode - reset form
        setFormData({
          title: '',
          question: '',
          answer: '',
          category: '',
          tags: '',
          status: 'draft',
          roleId: null
        });
      }
      setErrors({});
      setSelectedFiles([]);
    }
  }, [isOpen, article]);

  const loadRoles = async () => {
    // Prevent duplicate calls
    if (loadingRoles) {
      return;
    }
    
    // If roles are already loaded, don't reload
    if (roles.length > 0) {
      return;
    }
    
    setLoadingRoles(true);
    try {
      const response = await registrationService.getRoles();
      //console.log('Roles response:', response);
      
      // registrationService.getRoles() already filters SUPER_ADMIN and formats roles
      // Response structure: { data: [{ value, label, roleId, roleCode }, ...] }
      let rolesData = [];
      
      if (response?.data && Array.isArray(response.data)) {
        rolesData = response.data;
      } else if (Array.isArray(response)) {
        rolesData = response;
      }
      
      // Roles are already formatted by registrationService.getRoles() as:
      // { value, label, roleId, roleCode }
      // Just need to map to our dropdown format: { roleId, roleName }
      const formattedRoles = rolesData.map(role => ({
        roleId: role.roleId,
        roleName: role.label || role.roleName || 'Unknown Role',
        roleCode: role.roleCode || role.value || ''
      })).filter(role => role.roleId); // Filter out any roles without roleId
      
      //console.log('Formatted roles for dropdown:', formattedRoles);
      setRoles(formattedRoles);
    } catch (error) {
      console.error('Error loading roles:', error);
      toast.error('Failed to load roles');
      setRoles([]);
    } finally {
      setLoadingRoles(false);
    }
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setSelectedFiles(prev => [...prev, ...files]);
  };

  const handleRemoveFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    } else if (formData.title.length > 500) {
      newErrors.title = 'Title must not exceed 500 characters';
    }

    if (!formData.question.trim()) {
      newErrors.question = 'Question is required';
    }

    if (!formData.answer.trim()) {
      newErrors.answer = 'Answer is required';
    }

    if (formData.category && formData.category.length > 100) {
      newErrors.category = 'Category must not exceed 100 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setSaving(true);
    try {
      // Parse tags from comma-separated string
      const tagsArray = formData.tags
        ? formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag.length > 0)
        : [];

      // Get current user info
      const currentUser = getCurrentUser();
      
      const articleData = {
        title: formData.title.trim(),
        question: formData.question.trim(),
        answer: formData.answer.trim(),
        category: formData.category.trim() || null,
        tags: tagsArray.length > 0 ? tagsArray : null,
        status: formData.status || 'draft',
        roleId: formData.roleId || null,
        createdBy: currentUser?.username || currentUser?.email || null
      };

      const savedArticle = await onSave(articleData);
      
      // Upload files if article was created and files are selected
      if (savedArticle && savedArticle.articleId && selectedFiles.length > 0) {
        setUploadingFiles(true);
        try {
          const uploadPromises = selectedFiles.map(file => 
            fileService.uploadFile(file, {
              reg_id: savedArticle.articleId,
              screenName: 'ARTICLE',
              fileTypeId: 1,
              isActive: true
            })
          );
          
          await Promise.all(uploadPromises);
          toast.success('Article and attachments created successfully!');
        } catch (error) {
          console.error('Error uploading files:', error);
          toast.error('Article created but some files failed to upload');
        } finally {
          setUploadingFiles(false);
        }
      }
      
      // Reset form on success (only if onSave doesn't throw)
      setFormData({
        title: '',
        question: '',
        answer: '',
        category: '',
        tags: '',
        status: 'draft',
        roleId: null
      });
      setErrors({});
      setSelectedFiles([]);
    } catch (error) {
      console.error('Error creating article in modal:', error);
      // Error handling and toast are done in parent component (handleSaveArticle)
      // Don't reset form on error so user can correct and retry
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={saving ? () => {} : onClose}
      title={article ? "Edit Article" : "Create New Article"}
      size="xl"
    >
      <form onSubmit={handleSubmit} id="create-article-form" className="space-y-6">
        {/* Title */}
        <div className="space-y-2">
          <Label htmlFor="title" className="flex items-center gap-2">
            <FileText className="h-4 w-4 text-blue-600" />
            Title <span className="text-red-500">*</span>
          </Label>
          <Input
            id="title"
            name="title"
            value={formData.title}
            onChange={handleChange}
            placeholder="Enter article title"
            className={errors.title ? 'border-red-500' : ''}
            maxLength={500}
            disabled={saving}
          />
          {errors.title && (
            <p className="text-sm text-red-500">{errors.title}</p>
          )}
          <p className="text-xs text-gray-500">{formData.title.length}/500 characters</p>
        </div>

        {/* Question */}
        <div className="space-y-2">
          <Label htmlFor="question" className="flex items-center gap-2">
            <FileText className="h-4 w-4 text-blue-600" />
            Question <span className="text-red-500">*</span>
          </Label>
          <Textarea
            id="question"
            name="question"
            value={formData.question}
            onChange={handleChange}
            placeholder="Enter the question this article answers"
            rows={3}
            className={errors.question ? 'border-red-500' : ''}
            disabled={saving}
          />
          {errors.question && (
            <p className="text-sm text-red-500">{errors.question}</p>
          )}
        </div>

        {/* Answer */}
        <div className="space-y-2">
          <Label htmlFor="answer" className="flex items-center gap-2">
            <FileText className="h-4 w-4 text-blue-600" />
            Answer <span className="text-red-500">*</span>
          </Label>
          <Textarea
            id="answer"
            name="answer"
            value={formData.answer}
            onChange={handleChange}
            placeholder="Enter the detailed answer/solution"
            rows={6}
            className={errors.answer ? 'border-red-500' : ''}
            disabled={saving}
          />
          {errors.answer && (
            <p className="text-sm text-red-500">{errors.answer}</p>
          )}
        </div>

        {/* Category and Status Row */}
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="category">Category</Label>
            <Input
              id="category"
              name="category"
              value={formData.category}
              onChange={handleChange}
              placeholder="Enter category (e.g., Technical Support)"
              list="categories-list"
              className={errors.category ? 'border-red-500' : ''}
              maxLength={100}
              disabled={saving}
            />
            {categories.length > 0 && (
              <datalist id="categories-list">
                {categories.map((cat, index) => (
                  <option key={index} value={cat} />
                ))}
              </datalist>
            )}
            {errors.category && (
              <p className="text-sm text-red-500">{errors.category}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="status">Status</Label>
            <select
              id="status"
              name="status"
              value={formData.status}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
              disabled={saving}
            >
              <option value="draft">Draft</option>
              <option value="published">Published</option>
              <option value="active">Active</option>
            </select>
          </div>
        </div>

        {/* Role Selection */}
        <div className="space-y-2">
          <Label htmlFor="roleId">Role</Label>
          <select
            id="roleId"
            name="roleId"
            value={formData.roleId ? String(formData.roleId) : ''}
            onChange={(e) => setFormData(prev => ({ ...prev, roleId: e.target.value ? parseInt(e.target.value) : null }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
            disabled={saving || loadingRoles}
          >
            <option value="">Select a role (optional)</option>
            {loadingRoles ? (
              <option disabled>Loading roles...</option>
            ) : roles.length > 0 ? (
              roles.map((role) => (
                <option key={role.roleId} value={String(role.roleId)}>
                  {role.roleName}
                </option>
              ))
            ) : (
              <option disabled>No roles available</option>
            )}
          </select>
          <p className="text-xs text-gray-500">Leave empty to make article available to all roles</p>
        </div>

        {/* Tags */}
        <div className="space-y-2">
          <Label htmlFor="tags" className="flex items-center gap-2">
            <Tag className="h-4 w-4 text-blue-600" />
            Tags
          </Label>
          <Input
            id="tags"
            name="tags"
            value={formData.tags}
            onChange={handleChange}
            placeholder="Enter tags separated by commas (e.g., password, security, user-management)"
            disabled={saving}
          />
          <p className="text-xs text-gray-500">Separate multiple tags with commas</p>
        </div>

        {/* File Upload Section */}
        <div className="space-y-2">
          <Label className="flex items-center gap-2">
            <Paperclip className="h-4 w-4 text-blue-600" />
            Attachments
          </Label>
          <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-4 hover:border-blue-500 transition-colors">
            <input
              type="file"
              id="file-upload"
              multiple
              onChange={handleFileChange}
              className="hidden"
              disabled={saving || uploadingFiles}
            />
            <label
              htmlFor="file-upload"
              className="flex flex-col items-center justify-center cursor-pointer"
            >
              <Upload className="h-8 w-8 text-gray-400 mb-2" />
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Click to upload or drag and drop
              </p>
              <p className="text-xs text-gray-500 mt-1">
                PDF, DOC, DOCX, XLSX, PNG, JPG, BMP, MP4
              </p>
            </label>
          </div>
          {selectedFiles.length > 0 && (
            <div className="space-y-2 mt-2">
              {selectedFiles.map((file, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between bg-gray-50 dark:bg-gray-800 p-2 rounded border border-gray-200 dark:border-gray-700"
                >
                  <span className="text-sm text-gray-700 dark:text-gray-300 truncate flex-1">
                    {file.name}
                  </span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => handleRemoveFile(index)}
                    className="h-6 w-6 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                    disabled={saving || uploadingFiles}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={saving}
            className="px-6"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form="create-article-form"
            disabled={saving || uploadingFiles}
            className="px-6 bg-blue-600 hover:bg-blue-700 text-white flex items-center gap-2"
          >
            {(saving || uploadingFiles) ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                {uploadingFiles ? 'Uploading files...' : (article ? 'Updating...' : 'Creating...')}
              </>
            ) : (
              <>
                <Save className="h-4 w-4" />
                {article ? 'Update Article' : 'Create Article'}
              </>
            )}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default CreateArticleModal;

